import courseIcon from '@/assets/course.svg';
import shareIcon from '@/assets/share.svg';
import jobList from '@/assets/jobList.svg';
import jupyterIcon from '@/assets/jupyter.svg';
import labIcon from '@/assets/lab.svg';
import workplace from '@/assets/workplace.svg';
import AppSetting from '@/components/appSetting';
import { fetchUserPermissions } from '@/utils/permissionsService';
import { getDiskUsage, DiskUsage } from '@/api/file';
import { websocketService, WSMessageType } from '@/utils/websocketService';
import { Layout, Menu, MenuProps, Modal, Progress, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from "react-router";
import { openDeniedDialog } from './deniedDialog';
import { useTranslation } from 'react-i18next';
const { Sider } = Layout;

const formatBytes = (bytes: number): string => {
    if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
    if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(2)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${bytes} B`;
};

const AppSideBar: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedKey, setSelectedKey] = useState<string>('WORKPLACE');
    const { t } = useTranslation('common');
    const [diskUsage, setDiskUsage] = useState<DiskUsage | null>(null);
    useEffect(() => {
        console.log(location.pathname.replace('/newlab', ''))
        switch (location.pathname.replace('/newlab', '')) {
            case '/jobList':
                setSelectedKey('JOBLIST');
                break;
            case '/jupyter':
                setSelectedKey('JUPYTER');
                break;
            case '/gui':
                setSelectedKey('GUI');
                break;
            case '/course':
                setSelectedKey('COURSE');
                break;
            case '/lab':
                setSelectedKey('LAB');
                break;
            case '/share/management':
                setSelectedKey('SHAREMANAGEMENT');
                break;
            default:
                setSelectedKey('WORKPLACE');
                break;
        }
    }, [location.pathname]);

    const jupyterClickHandler = async () => {
        const permissions = await fetchUserPermissions(true);
        if (permissions['jupyter'] === true) {
            navigate('/jupyter');
        } else {
            openDeniedDialog();
        }
    }

    const defaultItems: MenuProps['items'] = [
        {
            label: t('sidebar.workplace'),
            key: 'WORKPLACE',
            icon: <img src={workplace} alt="" className="svg-icon-dark" />,
            onClick: () => navigate('/')
        },
        {
            label: t('sidebar.job_list'),
            key: 'JOBLIST',
            icon: <img src={jobList} alt="" className="svg-icon-dark" />,
            onClick: () => navigate('/jobList')
        },
        {
            label: t('sidebar.course'),
            key: 'COURSE',
            icon: <img src={courseIcon} alt="" className="svg-icon-dark" />,
            onClick: () => navigate('/course')
        },
        {
            label: t('sidebar.jupyter'),
            key: 'JUPYTER',
            icon: <img src={jupyterIcon} alt="" className="svg-icon-dark" />,
            onClick: jupyterClickHandler
        }
    ]

    const [menuItems, setMenuItems] = useState<MenuProps['items']>(defaultItems);
    useEffect(() => {
        (async () => {
            const permissions = await fetchUserPermissions(true);
            const items: MenuProps['items'] = [...defaultItems];
            if (permissions['share'] === true) {
                items.push({
                    label: t('sidebar.share_management'),
                    key: 'SHAREMANAGEMENT',
                    icon: <img src={shareIcon} alt="" className="svg-icon-dark" />,
                    onClick: () => navigate('/share/management')
                });
            }
            setMenuItems(items);
        })()

    }, []);

    // The lab dashboard is served by the Protomia gateway; the tab shows only where that gateway serves it,
    // so the backend's own switch (P2_LAB_DASHBOARD=0, or no lab roster on the host) hides the tab too.
    const [labAvailable, setLabAvailable] = useState(false);
    useEffect(() => {
        const ctl = new AbortController();
        const timer = setTimeout(() => ctl.abort(), 8000);
        fetch(`${import.meta.env.VITE_PROTOMIA_URL ?? ''}/api/lab/`, { signal: ctl.signal, cache: 'no-store' })
            .then(res => setLabAvailable(res.ok))
            .catch(() => setLabAvailable(false))
            .finally(() => clearTimeout(timer));
        return () => { clearTimeout(timer); ctl.abort(); };
    }, []);

    const withLab = (items: MenuProps['items']): MenuProps['items'] => {
        if (!labAvailable || !items) return items;
        const lab = {
            label: t('sidebar.lab'),
            key: 'LAB',
            icon: <img src={labIcon} alt="" className="svg-icon-dark" />,
            onClick: () => navigate('/lab')
        };
        const at = items.findIndex(item => item?.key === 'JUPYTER') + 1;
        return [...items.slice(0, at), lab, ...items.slice(at)];
    };

    useEffect(() => {
        getDiskUsage().then(res => {
            setDiskUsage(res);
            if (res?.used_bytes && res?.max_storage_bytes && res.used_bytes / res.max_storage_bytes >= 1 && !sessionStorage.getItem('disk_usage_warning_shown')) {
                Modal.warning({
                    title: t('sidebar.disk_usage_warning'),
                });
                sessionStorage.setItem('disk_usage_warning_shown', '1');
            }
        });
    }, []);

    useEffect(() => {
        const handler = (payload: any) => {
            if (payload?.scope === 'disk_usage') {
                getDiskUsage().then(res => {
                    setDiskUsage(res);
                    if (res?.used_bytes && res?.max_storage_bytes && res.used_bytes / res.max_storage_bytes >= 1 && !sessionStorage.getItem('disk_usage_warning_shown')) {
                        Modal.warning({
                            title: t('sidebar.disk_usage_warning'),
                        });
                        sessionStorage.setItem('disk_usage_warning_shown', '1');
                    }
                });
            }
        };
        const unsubscribe = websocketService.on(WSMessageType.DISK_USAGE_REFRESH, handler);
        return unsubscribe;
    }, []);

    const diskPercent =
        diskUsage && diskUsage.max_storage_bytes > 0
            ? Math.min(100, Math.round((diskUsage.used_bytes / diskUsage.max_storage_bytes) * 100))
            : null;

    return (
        <Sider width={70} className=' bg-neutral-200 dark:bg-[#333333]'>
            <div className=' h-full overflow-hidden flex flex-col items-center justify-between'>
                <Menu
                    mode="inline"
                    selectedKeys={[selectedKey]}
                    className=' bg-neutral-200 dark:bg-[#333333]'
                    inlineCollapsed={true}
                    style={{ borderRight: 0 }}
                    items={withLab(menuItems)}
                />
                <div className='flex flex-col items-center gap-2 pb-1'>
                    {diskPercent !== null && (
                        <Tooltip
                            title={
                                <div>
                                    <div>{t('sidebar.disk_usage_used', { used: formatBytes(diskUsage!.used_bytes), max: formatBytes(diskUsage!.max_storage_bytes) })}</div>
                                    <div className='mt-1 text-yellow-300'>{t('sidebar.disk_usage_quota_warning')}</div>
                                    <div className='mt-1 text-gray-300'>{t('sidebar.disk_usage_hidden_files')}</div>
                                </div>
                            }
                            placement="right"
                        >
                            <Progress
                                type="circle"
                                percent={diskPercent}
                                size={44}
                                strokeColor={diskPercent >= 90 ? '#ff4d4f' : diskPercent >= 70 ? '#faad14' : '#52c41a'}
                            />
                        </Tooltip>
                    )}
                    <AppSetting />
                </div>
            </div>
        </Sider>
    );
};

export default AppSideBar;